import { EventEmitter } from '@timing71/common';

import { API_ROOT, CONFIG_UPDATE, RACE_CONTROL_MESSAGE, SESSION_UPDATE, STATE_UPDATE } from './constants.js';
import { SessionMonitor } from './sessionMonitor.js';

const SESSION_UPDATE_INTERVAL = 30 * 1000; // ms

export class Client extends EventEmitter {

  constructor({ series, season=null, eventID, sessionID }) {
    super();
    this.series = series;
    this.season = season || new Date().getFullYear();
    this.eventID = eventID;
    this.sessionID = sessionID;

    this._session_id = null;
    this._config = {};

    this._sessionMonitor = null;
    this._sessionTimeout = null;

    this._getCurrentEvent = this._getCurrentEvent.bind(this);
    this._getCurrentSession = this._getCurrentSession.bind(this);
    this._handleConfig = this._handleConfig.bind(this);
    this._handleMessage = this._handleMessage.bind(this);
    this._handleUpdate = this._handleUpdate.bind(this);
    this.updateSession = this.updateSession.bind(this);
  }

  async start(connectionService) {
    if (!this.eventID) {
      const currentEvent = await this._getCurrentEvent(connectionService);
      if (currentEvent) {
        this.updateSession(connectionService, currentEvent);
      }
    }
    else {
      const specifiedEvent = await this._getEventById(connectionService, this.eventID);
      if (specifiedEvent) {
        this.updateSession(connectionService, specifiedEvent);
      }
    }
  }

  async updateSession(connectionService, event) {
    const currentSession = await this._getCurrentSession(connectionService, event);
    if (currentSession && currentSession.full_id !== this._session_id) {
      this.emit('SESSION_CHANGE');
      this._session_id = currentSession.full_id;
      if (this._sessionMonitor) {
        this._sessionMonitor.stop();
      }
      this._sessionMonitor = new SessionMonitor(
        connectionService,
        this.series,
        this.season,
        event,
        currentSession
      );

      this._sessionMonitor.on(CONFIG_UPDATE, this._handleConfig);
      this._sessionMonitor.on(SESSION_UPDATE, this._handleUpdate);
      this._sessionMonitor.on(RACE_CONTROL_MESSAGE, this._handleMessage);

      this._sessionMonitor.start();
    }

    this._sessionTimeout = setTimeout(
      () => this.updateSession(connectionService, event),
      SESSION_UPDATE_INTERVAL
    );
  }

  async _getCurrentEvent(connectionService) {
    const events = await connectionService.fetch(
      `${API_ROOT}/Events/ListEvents/${this.series}/${this.season}`
    );

    const eventsJson = JSON.parse(events);
    const now = Date.now() / 1000;

    const currentEvent = eventsJson.find(e => e.begin_epoch <= now && e.end_epoch >= now);
    if (currentEvent) {
      return currentEvent;
    }

    const startedEvents = eventsJson.filter(e => e.begin_epoch <= now);
    const recentEvent = startedEvents[startedEvents.length - 1];

    if (recentEvent) {
      console.warn(`No current events found, using recent event ${recentEvent.name} instead`);
      return recentEvent;
    }

    return null;
  }

  async _getEventById(connectionService, eventID) {
    const event = await connectionService.fetch(`${API_ROOT}/Events/GetEventById/${this.series}/${this.season}/${eventID}`);
    return JSON.parse(event);
  }

  async _getCurrentSession(connectionService, event) {

    if (this.sessionID) {
      const session = await this._getSessionById(connectionService, event.event_id, this.sessionID);
      return session;
    }
    else {
      const sessions = await connectionService.fetch(
        `${API_ROOT}/Session/ListSessions/${this.series}/${this.season}/${event.event_id}`
      );
      const sessionsJson = JSON.parse(sessions);
      const now = Date.now() / 1000;

      const openSessions = sessionsJson.filter(s => s.start_epoch <= now && (!s.end_epoch || s.end_epoch >= now));

      if (openSessions.length > 0) {
        return openSessions[openSessions.length - 1];
      }

      console.warn('No current session, using most recent one');
      return sessionsJson[sessionsJson.length - 1];
    }
  }

  async _getSessionById(connectionService, eventID, sessionID) {
    const session = await connectionService.fetch(`${API_ROOT}/Events/GetSessionById/${this.series}/${this.season}/${eventID}/${sessionID}`);
    return JSON.parse(session);
  }

  _handleConfig(cfg) {
    this._config = cfg?.cfg || {};
  }

  _handleUpdate(data) {
    this.emit(STATE_UPDATE, {...data, config: this._config});
  }

  _handleMessage(msg) {
    this.emit(RACE_CONTROL_MESSAGE, {
      message: msg.msg.slice(9),
      timestamp: msg.epoch_ms
    });
  }

  stop() {
    if (this._sessionTimeout) {
      clearTimeout(this._sessionTimeout);
      this._sessionTimeout = null;
    }
    if (this._sessionMonitor) {
      this._sessionMonitor.stop();
    }
  }
}
